import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, X, ChevronDown } from 'lucide-react'
import { STATIC_MODELS } from '../lib/models-static'
import type { Model } from '../lib/api'
import { api } from '../lib/api'
import { ProviderBadge, CocoonBadge } from '../components/ui'
import { haptics } from '../lib/haptics'

function formatPrice(usdPerToken: number): string {
  const perM = usdPerToken * 1_000_000
  if (perM < 0.01) return `$${perM.toFixed(3)}/M`
  if (perM < 1) return `$${perM.toFixed(2)}/M`
  return `$${perM.toFixed(1)}/M`
}

function formatCtx(ctx: number): string {
  if (ctx >= 1_000_000) return `${(ctx / 1_000_000).toFixed(0)}M`
  return `${(ctx / 1000).toFixed(0)}K`
}

const CATEGORY_EMOJI: Record<string, string> = {
  flagship: '🏆',
  everyday: '🤖',
  coding: '💻',
  research: '🔬',
  fast: '⚡',
}

function ModelPicker({
  value,
  onChange,
  models,
}: {
  value: Model | null
  onChange: (m: Model | null) => void
  models: Model[]
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [searchActive, setSearchActive] = useState(false)

  const filtered = models.filter(
    (m) =>
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.id.toLowerCase().includes(search.toLowerCase()),
  )

  if (open) {
    return (
      <div
        className="flex flex-col rounded-[14px] overflow-hidden"
        style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)', minHeight: 56 }}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <input
            className="flex-1 bg-transparent text-[14px] outline-none"
            style={{ color: 'var(--tg-theme-text-color)', caretColor: 'var(--tg-theme-accent-text-color)' }}
            placeholder="Search model..."
            value={search}
            readOnly={!searchActive}
            onTouchStart={() => setSearchActive(true)}
            onClick={() => setSearchActive(true)}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            onClick={() => { setOpen(false); setSearch(''); setSearchActive(false) }}
            className="p-1 active:opacity-70 transition-opacity"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            <X size={16} />
          </button>
        </div>
        <div
          className="overflow-y-auto"
          style={{ maxHeight: '45vh', borderTop: '1px solid var(--tg-theme-bg-color)' }}
        >
          {filtered.map((m) => (
            <button
              key={m.id}
              onClick={() => {
                haptics.impact('light')
                onChange(m)
                setOpen(false)
                setSearch('')
                setSearchActive(false)
              }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left active:opacity-70 transition-opacity"
              style={{ borderBottom: '1px solid var(--tg-theme-bg-color)' }}
            >
              <span className="flex-1 min-w-0">
                <span
                  className="block text-[14px] font-medium truncate"
                  style={{ color: 'var(--tg-theme-text-color)' }}
                >
                  {m.name}
                </span>
                <span
                  className="block text-[11px] font-mono truncate mt-0.5"
                  style={{ color: 'var(--tg-theme-hint-color)' }}
                >
                  {m.id}
                </span>
              </span>
              {m.id.startsWith('cocoon/') ? <CocoonBadge /> : <ProviderBadge modelId={m.id} size="sm" />}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <button
      onClick={() => { haptics.impact('light'); setOpen(true) }}
      className="w-full flex items-center gap-2 px-3 rounded-[14px] active:opacity-70 transition-opacity"
      style={{
        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
        minHeight: 56,
      }}
    >
      {value ? (
        <>
          <div className="flex-1 min-w-0 text-left">
            <p
              className="text-[14px] font-medium truncate"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              {value.name}
            </p>
            <p
              className="text-[11px] font-mono truncate mt-0.5"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              {value.id}
            </p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); haptics.impact('light'); onChange(null) }}
            className="p-1 active:opacity-70 transition-opacity shrink-0"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            <X size={16} />
          </button>
        </>
      ) : (
        <>
          <span
            className="flex-1 text-left text-[14px]"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            Select model
          </span>
          <ChevronDown size={16} style={{ color: 'var(--tg-theme-hint-color)' }} />
        </>
      )}
    </button>
  )
}

interface CompareRowProps {
  label: string
  a: string | null
  b: string | null
  highlight?: 'a' | 'b' | null
}

function CompareRow({ label, a, b, highlight }: CompareRowProps) {
  return (
    <div
      className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 py-3 px-4"
      style={{ borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
    >
      <span
        className={`text-[13px] text-right ${highlight === 'a' ? 'font-semibold' : ''}`}
        style={{
          color: highlight === 'a' ? 'var(--tg-theme-link-color)' : 'var(--tg-theme-text-color)',
        }}
      >
        {a ?? '—'}
      </span>
      <span
        className="text-[11px] text-center shrink-0 px-1"
        style={{ color: 'var(--tg-theme-hint-color)', minWidth: 80 }}
      >
        {label}
      </span>
      <span
        className={`text-[13px] ${highlight === 'b' ? 'font-semibold' : ''}`}
        style={{
          color: highlight === 'b' ? 'var(--tg-theme-link-color)' : 'var(--tg-theme-text-color)',
        }}
      >
        {b ?? '—'}
      </span>
    </div>
  )
}

export default function ComparePage() {
  const navigate = useNavigate()
  const [models, setModels] = useState<Model[]>(STATIC_MODELS)
  const [modelA, setModelA] = useState<Model | null>(null)
  const [modelB, setModelB] = useState<Model | null>(null)

  useEffect(() => {
    api.listModels()
      .then(setModels)
      .catch(() => { /* keep static fallback */ })
  }, [])

  const showComparison = modelA && modelB

  // Which model wins on price (lower = better)
  const priceWinner =
    showComparison
      ? modelA.pricing.prompt_usd_per_token <= modelB.pricing.prompt_usd_per_token
        ? 'a'
        : 'b'
      : null

  const ctxWinner =
    showComparison
      ? modelA.context_length >= modelB.context_length
        ? 'a'
        : 'b'
      : null

  const outPriceWinner =
    showComparison
      ? modelA.pricing.completion_usd_per_token <= modelB.pricing.completion_usd_per_token
        ? 'a'
        : 'b'
      : null

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header */}
      <div
        className="flex items-center shrink-0 px-sp-4"
        style={{ height: '48px' }}
      >
        <button
          onClick={() => { haptics.impact('light'); navigate(-1) }}
          className="flex items-center gap-1 active:opacity-70 transition-opacity"
          style={{ color: 'var(--tg-theme-accent-text-color)' }}
        >
          <ArrowLeft size={18} />
          <span className="text-[15px]">Models</span>
        </button>
        <span
          className="absolute left-1/2 -translate-x-1/2 text-[17px] font-semibold"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          Compare
        </span>
      </div>

      <div className="flex-1 overflow-y-auto scroll-area">
        {/* Pickers */}
        <div className="grid grid-cols-2 gap-sp-2 px-sp-4 pt-sp-4 pb-sp-3">
          <ModelPicker value={modelA} onChange={setModelA} models={models} />
          <ModelPicker value={modelB} onChange={setModelB} models={models} />
        </div>

        {/* Comparison table */}
        {showComparison && (
          <div>
            {/* Provider badges row */}
            <div
              className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 py-3 px-4"
              style={{ borderBottom: '1px solid var(--tg-theme-secondary-bg-color)' }}
            >
              <div className="flex justify-end">
                {modelA.id.startsWith('cocoon/') ? <CocoonBadge /> : <ProviderBadge modelId={modelA.id} size="sm" />}
              </div>
              <span
                className="text-[11px] text-center shrink-0 px-1"
                style={{ color: 'var(--tg-theme-hint-color)', minWidth: 80 }}
              >
                Provider
              </span>
              <div className="flex justify-start">
                {modelB.id.startsWith('cocoon/') ? <CocoonBadge /> : <ProviderBadge modelId={modelB.id} size="sm" />}
              </div>
            </div>

            <CompareRow
              label="Category"
              a={`${CATEGORY_EMOJI[modelA.category ?? ''] ?? ''} ${modelA.category ?? '—'}`}
              b={`${CATEGORY_EMOJI[modelB.category ?? ''] ?? ''} ${modelB.category ?? '—'}`}
            />
            <CompareRow
              label="Context"
              a={formatCtx(modelA.context_length)}
              b={formatCtx(modelB.context_length)}
              highlight={ctxWinner}
            />
            <CompareRow
              label="Input price"
              a={formatPrice(modelA.pricing.prompt_usd_per_token)}
              b={formatPrice(modelB.pricing.prompt_usd_per_token)}
              highlight={priceWinner}
            />
            <CompareRow
              label="Output price"
              a={formatPrice(modelA.pricing.completion_usd_per_token)}
              b={formatPrice(modelB.pricing.completion_usd_per_token)}
              highlight={outPriceWinner}
            />
            <CompareRow
              label="Description"
              a={modelA.description ?? null}
              b={modelB.description ?? null}
            />
          </div>
        )}

        {/* Empty state */}
        {!showComparison && (
          <div className="flex flex-col items-center justify-center py-16 px-8 gap-3">
            <p
              className="text-[32px]"
            >
              ⇄
            </p>
            <p
              className="text-[15px] font-medium text-center"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              Select two models to compare
            </p>
            <p
              className="text-[13px] text-center"
              style={{ color: 'var(--tg-theme-hint-color)' }}
            >
              Pricing, context window, and capabilities side by side
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
